// A new product: Omni creates it in Autotask in the same request (one-time as a
// Product, monthly as a Service) and reads it back. Opened from a OneBill
// product, it is linked to the plan ops choose, and that plan sets the billing.
// Opened from a quote document's line, it starts from what the line printed.
import React, { useState } from "react";
import { seedFloor, type Billing, type CatalogSyncState, type ProductInput } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { parseDollars } from "../../format.js";
import { Callout, Sheet } from "../../ui.js";
import { categoriesOf, planText, usablePlans, withoutSuffix, type OneBillRow, type Product } from "./model.js";
import { PriceBox } from "./parts.js";

/** What a quote document's line printed, for the form to start from. */
export interface AddStart {
  name: string;
  code: string;
  billing: Billing;
  /** Dollars, like "19.00", or empty. */
  listPrice: string;
}

export function AddSheet({
  from,
  start = null,
  list,
  checked,
  busy,
  problem,
  onClose: close,
  create,
}: {
  from: OneBillRow | null;
  start?: AddStart | null;
  /** Every product Omni has, for the name check and the monthly categories. */
  list: readonly Product[];
  /** The last catalog check, for Autotask's product categories. */
  checked: CatalogSyncState | null;
  busy: boolean;
  problem: string | null;
  onClose: () => void;
  create: (input: ProductInput) => Promise<boolean>;
}): React.ReactElement {
  const plans = from ? usablePlans(from) : [];
  const [planId, setPlanId] = useState(plans[0]?.id ?? "");
  const plan = plans.find((pl) => pl.id === planId) ?? null;
  const [chosenBilling, setChosenBilling] = useState<Billing>(start?.billing ?? "one_time");
  const billing: Billing = plan ? plan.billing : chosenBilling;
  const [name, setName] = useState(from ? withoutSuffix(from.name) : (start?.name ?? ""));
  const [code, setCode] = useState(from ? withoutSuffix(from.productCode) : (start?.code ?? ""));
  const [category, setCategory] = useState((from ? plan?.billing : start?.billing) === "monthly" ? "Services" : "");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("");
  const [listPrice, setListPrice] = useState(plan ? (plan.amount / 100).toFixed(2) : (start?.listPrice ?? ""));
  const [unitCost, setUnitCost] = useState("");
  const [floor, setFloor] = useState("");
  const [buy, setBuy] = useState("");
  const [errors, setErrors] = useState<{ name?: string; code?: string; category?: string; list?: string; other?: string }>({});
  const atCategories = checked?.categories && checked.categories.length > 0 ? checked.categories : [];
  const categories = billing === "one_time" ? atCategories : categoriesOf(list);

  function pickPlan(id: string): void {
    const next = plans.find((pl) => pl.id === id);
    if (!next) return;
    setPlanId(id);
    setListPrice((next.amount / 100).toFixed(2));
  }

  async function submit(): Promise<void> {
    const trimmed = name.trim();
    const listCents = parseDollars(listPrice);
    const optional = (text: string) => (text.trim() === "" ? null : parseDollars(text));
    const found: typeof errors = {};
    if (!trimmed) found.name = "Give it a name.";
    else if (list.some((p) => p.name.trim().toLowerCase() === trimmed.toLowerCase())) found.name = `Omni already has a product named \u201c${trimmed}\u201d.`;
    // Omni won't create a product without a code (catalog/write.ts on the server), so it is asked for here, not refused after.
    if (!code.trim()) found.code = "Give it a product code.";
    if (!category) found.category = "Choose a category.";
    if (listCents === null) found.list = "Enter a list price, like 19.00.";
    const costCents = optional(unitCost);
    const floorCents = optional(floor);
    const buyCents = optional(buy);
    if ((unitCost.trim() && costCents === null) || (floor.trim() && floorCents === null) || (buy.trim() && buyCents === null)) found.other = "Prices need to be amounts in dollars, like 19.00.";
    setErrors(found);
    if (Object.keys(found).length || listCents === null) return;
    const input: ProductInput = {
      name: trimmed,
      description: description.trim(),
      code: code.trim(),
      category,
      billing,
      unit: unit.trim(),
      listPrice: listCents,
      unitCost: costCents,
      floorPrice: floorCents ?? seedFloor(listCents),
      wholesaleCost: buyCents,
      enabled: false,
      active: true,
      ...(from && plan ? { onebill: { productCode: from.productCode, planId: plan.id } } : {}),
    };
    if (await create(input)) close();
  }

  return (
    <Sheet
      title={from ? "Add to Omni from OneBill" : "Add product"}
      wide
      onClose={close}
      foot={
        <>
          <button className="btn" type="button" data-testid="add-cancel" onClick={close}>
            Cancel
          </button>
          <button className="btn primary" type="button" disabled={busy || (from !== null && !plan)} data-testid="add-save" onClick={() => void submit()}>
            <Icon name="plus" size={15} />
            {busy ? "Adding" : "Add product"}
          </button>
        </>
      }
    >
      <div className="omni-cat" data-testid="add-sheet">
        {problem ? (
          <Callout kind="stop" icon="triangle-alert">
            {problem}
          </Callout>
        ) : null}
        {from && plan ? (
          <div className="callout gap" data-testid="add-from">
            <Icon name="link" size={15} />
            <span>
              Linked to OneBill <b>{from.name}</b>, plan {plan.id}, {planText(plan)}.
            </span>
          </div>
        ) : null}
        <div className="callout ok" data-testid="add-rule">
          <Icon name="info" size={15} />
          <span>
            <b>One-time becomes an Autotask Product, monthly a Service.</b> Omni creates it in Autotask when you add it, then reads it back. Billing can{"\u2019"}t change later.
          </span>
        </div>
        <div className="form2 gap">
          <label className="field full">
            <span>Name</span>
            <input className="input" data-testid="a-name" value={name} onChange={(e) => setName(e.target.value)} />
            {errors.name ? <span className="err">{errors.name}</span> : null}
          </label>
          {from ? (
            <div className="field full">
              <span>OneBill plan</span>
              <div className="choice" role="radiogroup" aria-label="OneBill plan">
                {plans.map((pl) => (
                  <label key={pl.id}>
                    <input type="radio" name="a-plan" value={pl.id} checked={pl.id === planId} onChange={() => pickPlan(pl.id)} />
                    <span>
                      <b>{pl.name}</b>
                      <span>
                        Plan {pl.id} · {planText(pl)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <div className="field full">
            <span>Billed</span>
            <div className="choice" role="radiogroup" aria-label="Billed">
              <label>
                <input
                  type="radio"
                  name="a-bill"
                  value="one_time"
                  data-testid="a-bill-one_time"
                  checked={billing === "one_time"}
                  disabled={from !== null}
                  onChange={() => {
                    setChosenBilling("one_time");
                    if (category === "Services") setCategory("");
                  }}
                />
                <span>
                  <b>One-time</b>
                  <span>An Autotask Product</span>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="a-bill"
                  value="monthly"
                  data-testid="a-bill-monthly"
                  checked={billing === "monthly"}
                  disabled={from !== null}
                  onChange={() => {
                    setChosenBilling("monthly");
                    if (!category) setCategory("Services");
                  }}
                />
                <span>
                  <b>Monthly</b>
                  <span>An Autotask Service</span>
                </span>
              </label>
            </div>
            {from ? <span className="hint">Set by the OneBill plan.</span> : null}
          </div>
          <label className="field" data-testid="add-code">
            <span>Product code</span>
            <input className="input" data-testid="a-code" placeholder="Required" aria-required="true" value={code} onChange={(e) => setCode(e.target.value)} />
            {errors.code ? <span className="err">{errors.code}</span> : null}
            <span className="hint" data-testid="a-code-hint">
              The USOC Phoneware bills it under, like EDGBAS. Omni writes it to Autotask when it creates the product and links OneBill by it. After that Omni reads it from Autotask, so change it there.
            </span>
          </label>
          <label className="field">
            <span>Category</span>
            <select className="input" data-testid="a-category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="" disabled>
                Choose a category
              </option>
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <span className="hint">{billing === "monthly" ? "Kept in Omni only: Autotask Services have no category." : "Written to the Autotask Product."}</span>
            {errors.category ? <span className="err">{errors.category}</span> : null}
          </label>
          <label className="field full">
            <span>Description</span>
            <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="field">
            <span>List price</span>
            <PriceBox id="a-list" label="List price" value={listPrice} onChange={setListPrice} />
            <span className="hint">{billing === "monthly" ? "A month." : "Once."}</span>
            {errors.list ? <span className="err">{errors.list}</span> : null}
          </label>
          <label className="field">
            <span>Unit cost</span>
            <PriceBox id="a-cost" label="Unit cost" value={unitCost} placeholder="Not set" onChange={setUnitCost} />
          </label>
          <label className="field">
            <span>Unit</span>
            <input className="input" data-testid="a-unit" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </label>
          <label className="field">
            <span>Floor</span>
            <PriceBox id="a-floor" label="Floor" value={floor} placeholder="80% of list if empty" onChange={setFloor} />
            <span className="hint">Dealers quote down to this without approval.</span>
          </label>
          <label className="field">
            <span>Reseller buy price</span>
            <PriceBox id="a-buy" label="Reseller buy price" value={buy} placeholder="Not set" onChange={setBuy} />
            <span className="hint">Resellers can{"\u2019"}t quote it until it has one.</span>
          </label>
        </div>
        {errors.other ? <p className="err">{errors.other}</p> : null}
        {from ? null : <p className="hint gap">It needs a OneBill link before dealers can quote it. Omni puts it in the review queue.</p>}
      </div>
    </Sheet>
  );
}
